'use strict'

import State from 'ampersand-state'
import Collection from 'ampersand-rest-collection'
import protooClient from 'protoo-client'
import * as mediasoupClient from 'mediasoup-client'
import Promise from 'bluebird'
import randomstring from 'randomstring'
import { PeerCollection, PeerModel } from './greenroom-peer-model'
import { MediaCollection, MediaModel } from './greenroom-media-model'
import MessageModel from './greenroom-message-model'
import Observable from './_observable'
import { assign, debounce, defaultsDeep, delay, each, extend, filter, find, flatten, get, groupBy, map, pick, result } from 'lodash/fp'

const ROOM_OPTIONS = {
  requestTimeout: 10000,
  transportOptions: {
    tcp: false
  }
}
const STATE = {
  connecting: 'connecting',
  preparing: 'preparing',
  connected: 'connected',
  reconnecting: 'reconnecting',
  leaving: 'leaving',
  closed: 'closed',
}

async function getProtooUrl (peerId, roomId, hostname, port, attempt = 0) {
  try {
    const response = await fetch(`https://${hostname}/api/webrtc/room/${roomId}`, {
      headers: new Headers({'Content-Type': 'application/json'})
    })
    const {host} = await response.json()
    if (host) {
      hostname = host
    }
  } catch (e) {
    // 404 Room does not exist
  }

  return `wss://${hostname}:${port}/?peer-id=${peerId}&room-id=${roomId}&attempt=${attempt}`
}

async function getTurnServers (device) {
  try {
    const response = await fetch('/api/greenroom/turn', {
      credentials: 'same-origin'
    })
    let servers = await response.json()
    if (Array.isArray(servers)) {
      // todo: remove this grooos edge hack
      // as of 6/6/2018 edge ONLY supports a set ice candidates that are ONE stun, ONE udp turn, and ONE tcp turn
      if (device && device.flag === 'msedge') {
        servers = [
          servers.find(server => server.url.indexOf('stun:') === 0),
          servers.find(server => server.url.indexOf('turn:') === 0 && server.url.indexOf('?transport=udp') !== -1),
          servers.find(server => server.url.indexOf('turn:') === 0 && server.url.indexOf('?transport=tcp') !== -1)
        ].filter(server => !!server)
      }

      // assign modern "urls" property
      servers.forEach(server => server.urls = (server.urls || server.url))

      /** cap the number of servers to 2 to prevent the following warnings (in FF):
       *   - Using five or more STUN/TURN servers causes problems
       *   - Using more then two STUN/TURN servers slows down discovery
       *   - Using only one STUN/TURN server could limit your potential
       */
      servers.splice(2)

      return servers
    }
    return []
  } catch (e) {
    window.logger && window.logger.warn(`An error occurred while attempting to retrieve a list of turn servers: ${e.message}`)
  }
}

const RoomModel = State.extend(Observable).extend({
  SHARED_PROPS: ['isHost', 'displayName', 'device', 'isUser', 'isVisible', 'webcamDeviceId', 'microphoneDeviceId', 'screenDeviceId', 'audioDeviceId'],
  TRACK_PROPS_ROOM: ['id', 'isHost', 'displayName', 'guestCount', 'peerId'],
  TRACK_PROPS_TRANSPORT: ['id', '_settings'],
  props: {
    id: 'string',
    code: 'string',
    peerId: 'string',
    protoo: 'object',
    mediasoupRoom: 'object',
    displayName: 'string',
    myPeer: 'state',
    manager: 'state',
    device: ['object', true, () => mediasoupClient.getDeviceInfo()],
    state: ['string', true, STATE.closed], // connecting/preparing/connected/reconnecting/closed
    isUser: ['boolean', true, false], // Is this a human peer or an external service feed?
    isHost: ['boolean', true, false],
    isVisible: ['boolean', true, false],
    isHostJoined: ['boolean', true, false],
    ignoreRemoteDeviceIds: ['boolean', true, false],
    ownerName: 'string',
    serverHostname: 'string',
    serverPort: 'number',
    turnServers: ['array', false],
    canSendMic: ['boolean', true, false],
    canSendWebcam: ['boolean', true, false],
    restartIceInProgress: ['boolean', true, false],
    maxGuests: ['number', true, 8],
    guestCount: ['number', true, 0],
    userCount: ['number', true, 0],
    activeSpeaker: 'string'
  },
  collections: {
    eventMessages: Collection.extend({model: MessageModel, comparator: 'timestamp'}),
    chatMessages: Collection.extend({model: MessageModel, comparator: 'timestamp'}),
    peers: PeerCollection,
    media: MediaCollection
  },
  derived: {
  },
  initialize (options = {}) {
    const logger = options.logger || window.logger
    this.logger = logger.addNamespace('Greenroom:Room', ['webrtc, greenroom'])
    this.validateConsumerContext = options.validateConsumerContext || (() => true)
    this.peerId = this.peerId || randomstring.generate()
    this.listenToAndRun(this.peers, 'change', () => {
      this.isHostJoined = !!this.peers.find({isHost: true, isVisible: true})
      this.guestCount = this.peers.filter({isUser: true, isVisible: true}).length
      this.userCount = this.guestCount + (this.isUser && this.isVisible ? 1 : 0)
    })

    const myPeerChangeEvents = map(x => `change:${x}`)(this.SHARED_PROPS)

    const localPeerChange = props => {
      this.myPeer.set(props)
      this.set(props)
      this.peers.trigger('change')
    }

    // Set local peer data
    const peerData = pick(this.SHARED_PROPS)(this)
    peerData.isLocal = true
    peerData.id = this.peerId
    peerData.addMedia = (...args) => this.addMedia(...args)
    this.myPeer = new PeerModel(peerData)

    const debouncedSendPeerData = debounce(600)(this.sendPeerData.bind(this))
    this.myPeer.on(myPeerChangeEvents.join(' '), () => localPeerChange(pick(this.SHARED_PROPS)(this.myPeer)))
    this.on(myPeerChangeEvents.join(' '), () => localPeerChange(pick(this.SHARED_PROPS)(this)))
    this.peers.on('change', debouncedSendPeerData, this)

    if (!this.displayName) {
      this.displayName = this.isHost ? 'Host' : 'Guest'
    }

    this.on('change:state', () => {
      this.logger.info('Greenroom state: ' + this.state + '...', this.id)
      this.trigger(`state:${this.state}`)
    })
  },
  sendPeerData () {
    const props = pick(this.SHARED_PROPS)(this.myPeer)
    this.sendEvent('peerdata', props)
  },
  leave () {
    this.logger.debug(`leave()`, ...arguments)
    if (this.state === STATE.closed) return
    this.state = STATE.leaving
    this.close()
  },
  close () {
    this.logger.debug(`close()`, ...arguments)
    if (this.mediasoupRoom) {
      this.mediasoupRoom.leave()
      delete this.mediasoupRoom
    }
    if (this.protoo) {
      // Alias the protoo so it doesn't get hijacked by an open() call
      this.protoo.close()
      delete this.protoo
    }
  },
  getTrackingProps () {
    return Object.assign({},
      pick(this.TRACK_PROPS_ROOM, this), {
        sendTransport: pick(this.TRACK_PROPS_TRANSPORT, this._sendTransport),
        recvTransport: pick(this.TRACK_PROPS_TRANSPORT, this._recvTransport),
        hasMultipleWebsocketConnections: result('app.model.numWebsocketConnections', window) > 1,
        device: this.manager.device.bowser,
        webcamsAvailable: this.manager.webcams.map(pick(['deviceId', 'displayName'])),
        microphonesAvailable: this.manager.microphones.map(pick(['deviceId', 'displayName'])),
        producers: this.mediasoupRoom && this.mediasoupRoom.producers.map(result('appData'))
      })
  },
  async open () {
    this.logger.info('open()', this.getTrackingProps())
    if (this.state !== STATE.closed) return
    this.state = STATE.connecting
    this.logger.debug(`Assigned peer id to user`, {peerId: this.peerId})
    this.slowConnectionTimeout = delay(2500)(() => this.status({status: 'Attempting to connect to media server...'}))

    // Initialize mediasoup
    const turnServers = this.turnServers || (this.turnServers = await getTurnServers(this.manager.device))
    this.mediasoupRoom = new mediasoupClient.Room(Object.assign({turnServers}, ROOM_OPTIONS))

    // Initiate transport
    const protooUrl = await getProtooUrl(this.peerId, this.id, this.serverHostname, this.serverPort)
    this.protooTransport = new protooClient.WebSocketTransport(protooUrl, {retry: {factor: 2, forever: true, minTimeout: 100, maxTimeout: 10000}})
    this.protoo = new protooClient.Peer(this.protooTransport)

    // Protoo event handlers
    this.protoo.on('open', () => {
      window.clearTimeout(this.slowConnectionTimeout)
      this.trigger('status:resolve')
      this.logger.debug('protoo Peer "open" event')
      try {
        this._joinRoom()
      } catch (e) {

      }
    })
    this.protoo.on('disconnected', () => {
      this.logger.warn('protoo Peer "disconnected" event')
      // Leave Room
      try {
        this.mediasoupRoom.remoteClose({cause: 'protoo disconnected'})
      }
      catch (error) {
      }

      this.state = STATE.reconnecting
      this.status({status: 'Connection to media server interrupted...'})
      this.once('state:connected', () => this.notification('Connection to media server has been restored'))
    })
    this.protoo.on('failed', async attempt => {
      this.logger.warn('protoo Peer "failed" event attempt ' + attempt)
      if (this.protoo._transport) {
        this.protoo._transport._url = await getProtooUrl(this.peerId, this.id, this.serverHostname, this.serverPort, attempt)
      }
    })
    this.protoo.on('close', () => {
      if (this.state !== STATE.closed) this.logger.warn('protoo Peer "close" event')
      if (this.state === STATE.connecting || this.state === STATE.reconnecting || this.state === STATE.preparing) {
        // Failed on join
        this.status({status: 'Network issues connecting to Lightstream. Please use Support chat to report & troubleshoot.', action: () => this.manager.joinRoom(this.id)})
        this.logger.critical('Greenroom connection failure', this.getTrackingProps())
        if (window.analytics) {
          window.analytics.track('Greenroom connection failure', this.getTrackingProps())
        }
      } else if (this.state !== STATE.closed && this.state !== 'leaving') {
        // Connection interrupted after initial successful connection
        this.trigger('close:unexpected')

        // attempt to reconnect
        setTimeout(() => this.open(), 1000) // @todo: fix this bad code smell
      }
      if (this.state !== STATE.reconnecting) {
        this.state = STATE.closed
      }
      this.close()
    })
    this.protoo.on('request', this._handleProtooRequest.bind(this))
  },
  _handleProtooRequest (request, accept, reject) {
    this.logger.debug('_handleProtooRequest()', request)
    switch (request.method) {
      case 'mediasoup-notification': {
        accept()
        const notification = request.data
        this.logger.info(request.method, notification)
        try {
          this.mediasoupRoom.receiveNotification(notification)
        } catch (e) {
          this.logger.warn('Failed to handle mediasoup notification', {notification, error: e})
        }
        break
      }
      case 'active-speaker': {
        accept()
        const {peerName: peerId} = request.data
        if (peerId === this.peerId) {
          // Is self
          this.activeSpeaker = 'Self'
        } else if (peerId) {
          // Is other
          const activePeer = this.peers.get(peerId)
          this.activeSpeaker = activePeer ? activePeer.displayName : null
        } else {
          this.activeSpeaker = null
        }
        break
      }
      case 'message': {
        accept()
        this._onMessage(request.data)
        break
      }
      default: {
        this.logger.error('Unknown protoo method/request', request.method)
        reject(404, 'unknown method')
      }
    }
  },
  async _joinRoom () {
    this.logger.debug('_joinRoom()')

    // Mediasoup event handlers.
    // We generally only listen to these -
    // Custom events should be sent through the protoo peer
    this.mediasoupRoom.removeAllListeners()
    this.mediasoupRoom.on('close', (originator, appData) => {
      if (originator === 'remote') {
        this.logger.warn('Pediasoup Peer/Room remotely closed', appData)
        this.state = STATE.closed
        this.open()
      }
    })
    this.mediasoupRoom.on('request', (request, callback, errback) => {
      this.logger.debug('sending mediasoup request', request)
      this.protoo.send('mediasoup-request', request)
        .then(callback)
        .catch(errback)
    })
    this.mediasoupRoom.on('notify', (notification) => {
      this.logger.debug('sending mediasoup notification', notification)
      this.protoo.send('mediasoup-notification', notification)
        .catch((error) => {
          this.logger.warn('could not send mediasoup notification', error)
        })
    })
    this.mediasoupRoom.on('newpeer', (peer) => {
      this.logger.debug('room "newpeer" event', peer)
      this._handlePeer(peer)
      if (peer.appData.isVisible && peer.appData.isUser) {
        this.notifyJoin(peer.appData.displayName)
      }
    })

    if (!this.isHost) {
      this.isVisible = true
    }
    try {
      const peerData = pick(this.SHARED_PROPS)(this.myPeer)
      await this.mediasoupRoom.join(this.peerId, peerData)
      await this._onJoin()
    } catch (e) {
      this.logger.error('_joinRoom() failed', e)
      this.trigger('close:unexpected')
      this.close()
    }
  },
  async _onJoin () {
    // Create Transport for sending.
    if (!this._sendTransport || this._sendTransport.closed) {
      this._sendTransport = this.mediasoupRoom.createTransport('send')
      this._sendTransport.on('close', (originator) => {
        this.logger.debug('Transport "close" event', {originator})
      })
      this._sendTransport.on('connectionstatechange', (state) => {
        if (state === 'failed') {
          this.logger.critical('Sending transport connection "failed"', this.getTrackingProps())
          this.close()
        } else {
          this.logger.info('Sending transport "connectionstatechange" event', {state})
        }
      })
    }

    // Create Transport for receiving.
    if (!this._recvTransport || this._recvTransport.closed) {
      this._recvTransport = this.mediasoupRoom.createTransport('recv')
      this._recvTransport.on('close', (originator) => {
        this.logger.debug('receiving Transport "close" event', {originator})
      })
      this._recvTransport.on('connectionstatechange', (state) => {
        if (state === 'failed') {
          this.logger.critical('Receiving transport connection "failed"', this.getTrackingProps())
          this.close()
        } else {
          this.logger.info('Receiving transport "connectionstatechange" event', {state})
        }
      })
    }

    // Set our media capabilities.
    this.set({
      canSendMic: this.mediasoupRoom.canSend('audio'),
      canSendWebcam: this.mediasoupRoom.canSend('video')
    })

    const peers = this.mediasoupRoom.peers
    for (const peer of peers) {
      this._handlePeer(peer, {notify: false})
    }
    this.sendEvent('peerdata', pick(this.SHARED_PROPS)(this.myPeer))

    each(x => {
      // Try to connect the media - we might have a producer/consumer for it this time
      this.addMedia(x)
    })(this.media.models)

    this.state = STATE.preparing
    await Promise.all(this.media.models.map(x => this.addMedia(x)))
    this.state = STATE.connected
  },
  _handlePeer (peer) {
    const {appData: sharedProps, name: id} = peer
    this.logger.info('Received peer', name, sharedProps)
    const addMedia = (...args) => this.addMedia(...args)
    const model = new PeerModel(Object.assign({id, allMedia: this.media, addMedia}, sharedProps))
    this.peers.add(model)

    // Hosts don't join on connect, since they are always connected - they join on change of isVisible
    if (model.isUser && !model.isVisible) {
      this.listenTo(model, 'change:isVisible', () => {
        if (model.isVisible) {
          this.notifyJoin(model.displayName)
        } else {
          this.notifyLeave(model.displayName)
        }
      })
    }

    for (const consumer of peer.consumers) {
      this._handleConsumer(consumer)
    }

    // Peer event handlers
    peer.on('close', originator => {
      this.logger.debug('peer "close" event', {peer}, {originator})
      this.peers.remove(model)
      if (this.mediasoupRoom.joined) {
        this.logger.info(`${model.displayName} left the room`)
        if (model.isUser && model.isVisible) {
          this.notifyLeave(model.displayName)
        }
      }
    })
    peer.on('newconsumer', consumer => {
      this.logger.debug('peer "newconsumer" event', name, {consumer})
      this._handleConsumer(consumer)
    })
  },
  async _handleConsumer (consumer) {
    this.logger.info('_handleConsumer()', consumer)

    const isValidConsumer = this.validateConsumerContext(consumer)
    if (!isValidConsumer) {
      return this.logger.info('ignoring consumer because it did not pass validation', consumer)
    }

    // TODO: This weird check prevents us from ingesting a screen's audio from the host.
    // This is not necessary on Firefox, but it was required in Chrome to prevent host
    // screen audio even though it was bound to no sink. See APP-504
    if (consumer.peer.appData.isHost && consumer.appData.type === 'audio' && this.isUser) return
    const codec = consumer.rtpParameters.codecs[0]

    // Attempt to find an existing consumer with the same device ID
    // We will use this to connect a media object to its consumer
    // in the event that the server restarts, or the peer reconnects
    const mediaData = Object.assign({
      consumer,
      id: consumer.id,
      codec: codec ? codec.name : null,
      peerId: consumer.peer.name,
      supported: consumer.supported,
      remotelyPaused: consumer.remotelyPaused
    }, consumer.appData)
    if (!mediaData.type) {
      mediaData.type = consumer.kind === 'audio' ? 'audioFeed' : 'videoFeed'
    }
    let model = await this.addMedia(mediaData)

    if (model.type === 'audioFeed' || model.type === 'videoFeed') {
      const feedPeer = this.peers.get(model.peerId)
      if (!feedPeer) {
        this.logger.critical('Could not find peer for feed', {peerId: model.peerId})
      }
      feedPeer[model.type] = model
    }


    // Consumer event handlers
    consumer.on('close', (originator) => {
      this.logger.debug('consumer "close" event', consumer, {originator})
      model.unset('track')
    })
    consumer.on('pause', (originator) => {
      this.logger.debug('consumer "pause" event', consumer, {originator})
      if (originator === 'local') {
        model.locallyPaused = true
      } else {
        model.remotelyPaused = true
      }
    })
    consumer.on('resume', (originator) => {
      this.logger.debug('consumer "resume" event', consumer, {originator})
      if (originator === 'local') {
        model.locallyPaused = false
      } else {
        model.remotelyPaused = false
      }
    })
    consumer.on('effectiveprofilechange', (profile) => {
      this.logger.debug('consumer "effectiveprofilechange" event', {consumer}, {profile})
      model.profile = profile
    })

    // Receive the consumer (if we can).
    if (consumer.supported) {
      consumer
        .receive(this._recvTransport)
        .then(track => {
          model.track = track
          this.trigger('consumers:updated')
        })
        .catch(error => {
          this.logger.error('unexpected error while receiving a new Consumer', error)
        })
    } else {
      this.logger.warn('Consumer not supported')
    }
  },
  getMatchingMedia ({deviceId, type, peerId, onlyActive = false}) {
    return this.media.find(x => {
      // TODO: This looks suspect
      return ((this.ignoreRemoteDeviceIds && this.peerId !== x.peerId) || !x.deviceId || !deviceId || x.deviceId === deviceId)
        && (x.type === type)
        && (x.peerId === peerId)
        && (!onlyActive || x.track)
    })
  },
  async addMedia (deviceOrMedia = {}) {
    // addMedia() requires a deviceId, peerId, and type
    // Media models are unique across these three points
    // It will attempt to return an existing or newly created media model.
    // We will use this to connect a media object to its consumer
    // in the event that the server restarts, or the peer reconnects.
    let {deviceId, type, displayName, screenType, peerId} = deviceOrMedia
    peerId = peerId || this.myPeer.id
    const isRemote = peerId !== this.myPeer.id
    let props = deviceOrMedia
    props.isRemote = isRemote
    if (!isRemote) {
      props.mediaDetails = this.device
    }

    if (deviceOrMedia.getAttributes) {
      props = deviceOrMedia.getAttributes({props: true, session: true, derived: true})
    }

    let model = this.getMatchingMedia({deviceId, type, peerId})
    if (model) {
      model.set(props)
    } else {
      model = new MediaModel(Object.assign(props, {id: randomstring.generate(), logger: this.logger, peerId}))
      this.media.add(model)
    }

    if (!isRemote && (!model.track || model.track.readyState === 'ended')) {
      model.unset('track')
      if (deviceId) {
        const device = this.manager.getDeviceByIdAndType(deviceId, type)
        if (device) {
          model.track = await device.getTrack()
        }
      }
    }

    if (isRemote || (this.state !== STATE.connected && this.state !== STATE.preparing)) {
      // We'll try to add it again once we're connected
      return model
    }

    if (!model.track) {
      return model // We will have to wait until it's added again, with a track
    }

    // Producer logic (consumers will never make it to this point)

    if (type === 'audio') {
      displayName = `${this.displayName}'s system audio`
    } else {
      const nameType = screenType || type
      displayName = `${this.displayName}'s ${nameType}`
    }

    const producer = this.mediasoupRoom.createProducer(model.track, {simulcast: this.manager.device.flag !== 'firefox'}, {
      // peers receive as consumer appData:
      deviceId,
      displayName,
      screenType,
      type,
      mediaDetails: this.device,
      peerId: this.myPeer.id
    })

    model.track.stop()

    if (deviceOrMedia.track) {
      deviceOrMedia.track = null // This will be set to the track returned by the producer
    }

    await producer.send(this._sendTransport)

    // Producer event handlers
    producer.on('close', (originator) => {
      this.logger.debug('Producer "close" event', {model, originator})
      model.unset('track')
    })
    producer.on('pause', (originator) => {
      this.logger.debug('Producer "pause" event', {model, originator})
      if (originator === 'local') {
        model.locallyPaused = true
      } else {
        model.remotelyPaused = true
      }
    })
    producer.on('resume', (originator) => {
      this.logger.debug('Producer "resume" event', {model, originator})
      if (originator === 'local') {
        model.locallyPaused = false
      } else {
        model.remotelyPaused = false
      }
    })
    producer.on('handled', () => {
      this.logger.debug('Producer "handled" event')
    })
    producer.on('unhandled', () => {
      this.logger.debug('Producer "unhandled" event')
    })

    let device
    if (type === 'webcam') {
      device = this.manager.webcams.get(deviceId)
    } else if (type === 'microphone') {
      device = this.manager.microphones.get(deviceId)
    }

    model.set({
      locallyPaused: producer.locallyPaused,
      remotelyPaused: producer.remotelyPaused,
      codec: producer.rtpParameters.codecs[0].name,
      track: producer.track,
      device,
      producer
    })

    return model
  },
  pushLocalChatAlert (text) {
    const timestamp = Date.now()
    const id = randomstring.generate()
    const alert = new MessageModel({
      id,
      timestamp,
      text,
      type: 'chat',
      isAlert: true
    })
    this.chatMessages.add(alert)
  },
  status ({status, action}) {
    // `status:resolve` will be triggered at a later time
    this.trigger('status', {status, action})
  },
  notification (notification, options = {}) {
    this.trigger('notification', notification, options)
  },
  notifyJoin (displayName) {
    if (this.state !== STATE.connected) return
    const notification = `${displayName} joined the room`
    this.pushLocalChatAlert(notification)
    this.notification(notification)
  },
  notifyLeave (displayName) {
    if (this.state !== STATE.connected) return
    const notification = `${displayName} left the room`
    this.pushLocalChatAlert(notification)
    this.notification(notification)
  },
  exit () {
    this.isVisible = false
    this.myPeer.setWebcam(null)
    this.myPeer.setMicrophone(null)
  },
  async enter () {
    this.isVisible = true
    await this.manager.updateDevices()
    if (!this.myPeer.webcam) {
      const webcam = this.getMatchingMedia({type: 'webcam', peerId: this.myPeer.id, onlyActive: true})
        || this.manager.getActiveOrFirstWebcam()
      if (webcam) {
        this.myPeer.setWebcam(webcam)
      } else {
        this.logger.warn('Entered room without a webcam')
      }
    }
    if (!this.myPeer.microphone) {
      const microphone = this.getMatchingMedia({type: 'microphone', peerId: this.myPeer.id, onlyActive: true})
        || this.manager.getActiveOrFirstMicrophone()
      if (microphone) {
        this.myPeer.setMicrophone(microphone)
      } else {
        this.logger.warn('Entered room without a microphone')
      }
    }
  },

  // Data channel
  _sendMessage ({type, info, destinationPeerId}) {
    if (this.state !== STATE.connected) return
    this.logger.info(`sendMessage()`, ...arguments)
    const timestamp = Date.now()
    const id = randomstring.generate()
    const props = {
      id,
      type,
      timestamp,
      peerId: this.peerId,
      username: this.displayName,
      isRemote: false,
      uri: info.uri,
      text: info.text,
      name: info.name,
      data: info.data || {}
    }
    const model = new MessageModel(props)
    model.peer = this.myPeer

    if (type === 'chat') {
      this.logger.debug('Sent chat message:', {message: props})
      this.chatMessages.add(model)
    } else if (type === 'event') {
      this.logger.debug('Sent event:', {event: props})
      this.eventMessages.add(model)
    }
    return this.protoo.send('message', {message: props, destinationPeerId})
  },
  _onMessage (props = {}) {
    props.peer = this.peers.get(props.peerId)

    if (!props.peer) {
      this.logger.warn('Received message from peer that could not be found', {peerId: props.peerId})
      return
    }

    delete props.peerId
    props.isRemote = true
    const model = new MessageModel(props)

    if (props.type === 'chat') {
      this.logger.debug('Received chat message:', {message: props})
      this.chatMessages.add(model)
    } else if (props.type === 'event') {
      this.logger.debug(`Received event: ${props.name}`, {event: props})
      this.eventMessages.add(model)
      const event = props.name
      switch (event) {
        // Message event handlers
        case 'disney': {
          if (!this.isHost) window.location = 'http://disney.com'
          break
        }
        case 'peerdata': {
          props.peer.set(props.data)
          break
        }
        default: {
          this.trigger(event, props.data)
        }
      }
    }
  },
  sendEvent (name = '', data = {}, destinationPeerId = null) {
    return this._sendMessage({type: 'event', info: {name, data}, destinationPeerId})
  },
  sendChatMessage (text = '', destinationPeerId = null) {
    return this._sendMessage({type: 'chat', info: {text}, destinationPeerId})
  },
  sendChatImage (uri = '', destinationPeerId = null) {
    return this._sendMessage({type: 'chat', info: {uri}, destinationPeerId})
  },
  banhammer () {
    if (this.isHost) {
      this.sendEvent('disney')
    }
  }
})

const RoomCollection = Collection.extend({
  model: RoomModel
})

export {RoomModel, RoomCollection}



// WEBPACK FOOTER //
// ./client/webrtc/greenroom-room-model.js